/**
 * Forecast Page
 *
 * Daily Gross Progression feature showing how department gross accumulates
 * throughout a month with historical comparison based on "working days remaining."
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  ForecastControlPanel,
  WorkingDaysProgress,
  GrossComparisonCards,
  GrossProgressionChart,
  UnitCountsCard,
  DepartmentTabs,
  DepartmentKey,
  ProjectionsTile,
  ServiceAdvisorCard,
  SalesManagerCard,
  SalespersonCard,
  DealDetailsModal,
} from '../components/forecast';
import { FIManagerCard } from '../components/forecast/FIManagerPerformance';
import { TechnicianCard } from '../components/forecast/TechnicianPerformance';
import { PartsCounterCard } from '../components/forecast/PartsCounterPerformance';
import { ServiceGrossBreakdown } from '../components/forecast/ServiceGrossBreakdown';
import { PartsGrossBreakdown } from '../components/forecast/PartsGrossBreakdown';
import { BodyShopGrossBreakdown } from '../components/forecast/BodyShopGrossBreakdown';
import { SalesDepartmentAnalysis } from '../components/forecast/SalesDepartmentAnalysis';
import { InventoryAgingCard } from '../components/forecast/InventoryAgingCard';
import { UsedInventoryAgingCard } from '../components/forecast/UsedInventoryAgingCard';
import { ConsolidatedServiceAnalysis } from '../components/forecast/ConsolidatedServiceAnalysis';
import { ConsolidatedServiceSortConfig } from '../types/consolidatedService';
import { ConsolidatedPartsAnalysis } from '../components/forecast/ConsolidatedPartsAnalysis';
import { ConsolidatedPartsSortConfig } from '../types/consolidatedParts';
import { useServiceAdvisorData } from '../hooks/useServiceAdvisorData';
import { useTechnicianData } from '../hooks/useTechnicianData';
import { usePartsCounterData } from '../hooks/usePartsCounterData';
import { useSalesManagerData } from '../hooks/useSalesManagerData';
import { useSalespersonData } from '../hooks/useSalespersonData';
import { useFIManagerData } from '../hooks/useFIManagerData';
import { useSalesLeadsData } from '../hooks/useSalesLeadsData';
import { useSalesAppointmentsData } from '../hooks/useSalesAppointmentsData';
import { useInventoryAgingData } from '../hooks/useInventoryAgingData';
import { useUsedInventoryAgingData } from '../hooks/useUsedInventoryAgingData';
import { useVautoLookToBookData } from '../hooks/useVautoLookToBookData';
import { useForecastData } from '../hooks/useForecastData';
import { useLocationParam } from '../hooks/useLocationParam';
import { DailyGrossEntry, DailyGrossSummary, DepartmentGrossValues } from '../types/forecast';
import { useAuth } from '../auth/AuthContext';
import { tsmDepartmentForLocation } from '../auth/msalConfig';
import { canRefreshData } from '../auth/permissions';
import { calculateTrendingGross } from '../utils/trendCalculation';
import { resolveSelectedDepartment } from './forecastDepartment';

/**
 * Get gross value for a specific department at a given working day
 * For "ALL" department, includes all departments including OTHERS
 */
function getGrossAtWorkingDay(
  summary: DailyGrossSummary | null,
  workingDayNumber: number,
  department: DepartmentKey
): number | null {
  if (!summary || !summary.dailyEntries || summary.dailyEntries.length === 0) return null;

  // Month-end aware lookup: entries are per calendar date, and postings can land on
  // trailing non-working days (accounting close JEs, e.g. dated Sunday the 31st).
  // At/past the highest working day present, use the chronologically LAST entry so
  // those trailing postings are included in the cumulative.
  const maxWD = summary.dailyEntries.reduce((m, e) => Math.max(m, e.workingDayNumber), 0);
  let entry: DailyGrossEntry | undefined;
  if (maxWD > 0 && workingDayNumber >= maxWD) {
    entry = summary.dailyEntries[summary.dailyEntries.length - 1];
  } else {
    // Find entry at this working day
    entry = summary.dailyEntries.find(e => e.workingDayNumber === workingDayNumber);

    // If exact match not found, find highest working day <= target
    if (!entry) {
      const validEntries = summary.dailyEntries
        .filter(e => e.isWorkingDay && e.workingDayNumber <= workingDayNumber)
        .sort((a, b) => b.workingDayNumber - a.workingDayNumber);

      if (validEntries.length > 0) {
        entry = validEntries[0];
      }
    }
  }

  if (!entry) return null;

  if (department === 'ALL') {
    const deptGross = entry.cumulativeDepartmentGross;
    if (!deptGross) return entry.cumulativeGross;

    // "All" department includes ALL departments including OTHERS
    return (deptGross.NEW_VEHICLE || 0) +
           (deptGross.USED_VEHICLE || 0) +
           (deptGross.SERVICE || 0) +
           (deptGross.PARTS || 0) +
           (deptGross.BODY_SHOP || 0) +
           (deptGross.RENTAL || 0) +
           (deptGross.OTHERS || 0);
  }

  // Get department-specific cumulative gross
  const deptKey = department as keyof DepartmentGrossValues;
  return entry.cumulativeDepartmentGross?.[deptKey] ?? null;
}

/**
 * Get final month gross for a specific department from DOC_GROSS_SUMMARY data
 * This uses accounting-based data that matches the Historical page
 * For "ALL" department, includes all departments including OTHERS
 */
function getDocFinalMonthGross(
  docGrossData: DepartmentGrossValues | null | undefined,
  department: DepartmentKey
): number | null {
  if (!docGrossData) return null;

  if (department === 'ALL') {
    // "All" department includes ALL departments including OTHERS
    return (docGrossData.NEW_VEHICLE || 0) +
           (docGrossData.USED_VEHICLE || 0) +
           (docGrossData.SERVICE || 0) +
           (docGrossData.PARTS || 0) +
           (docGrossData.BODY_SHOP || 0) +
           (docGrossData.RENTAL || 0) +
           (docGrossData.OTHERS || 0);
  }

  const deptKey = department as keyof DepartmentGrossValues;
  return docGrossData[deptKey] ?? null;
}

// Departments available to MarketingManager on Forecast page (excludes ALL and OTHERS)
const MARKETING_MANAGER_FORECAST_DEPTS = ['NEW_VEHICLE', 'USED_VEHICLE', 'SERVICE', 'PARTS', 'BODY_SHOP', 'RENTAL'];

export const Forecast: React.FC = () => {
  const { userRole, assignedDepartmentIds } = useAuth();

  // Initialize with current date
  const now = new Date();
  const { locationId, setLocationId } = useLocationParam();

  // Check if consolidated view (locationId=0) - no refresh for consolidated data (pre-calculated)
  const isConsolidated = locationId === 0;
  const showRefreshButton = canRefreshData(userRole) && !isConsolidated;
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // MarketingManager on Forecast gets specific dept list (no ALL, no OTHERS)
  const isMarketingManager = userRole === 'MarketingManager';
  const effectiveDepartmentIds = userRole === 'TeamServiceManager' && locationId > 0
    ? [tsmDepartmentForLocation(locationId)]
    : isMarketingManager
      ? MARKETING_MANAGER_FORECAST_DEPTS
      : assignedDepartmentIds;

  // DeptMgr with assigned departments defaults to first assigned; otherwise ALL
  const hasDeptRestriction = effectiveDepartmentIds && effectiveDepartmentIds.length > 0;
  const defaultDepartment: DepartmentKey = hasDeptRestriction
    ? (effectiveDepartmentIds[0] as DepartmentKey)
    : 'ALL';

  const [selectedDepartmentState, setSelectedDepartment] = useState<DepartmentKey>(defaultDepartment);
  const selectedDepartment = resolveSelectedDepartment(
    selectedDepartmentState,
    hasDeptRestriction ? effectiveDepartmentIds : undefined,
    defaultDepartment
  );
  const [dealDetailsModal, setDealDetailsModal] = useState<{
    isOpen: boolean;
    department: 'NEW_VEHICLE' | 'USED_VEHICLE';
  } | null>(null);

  // Collapsible service/parts sections - collapsed by default
  const [serviceAnalysisCollapsed, setServiceAnalysisCollapsed] = useState(true);
  const [partsAnalysisCollapsed, setPartsAnalysisCollapsed] = useState(true);
  const [serviceAdvisorCollapsed, setServiceAdvisorCollapsed] = useState(true);
  const [technicianCollapsed, setTechnicianCollapsed] = useState(true);
  const [partsCounterCollapsed, setPartsCounterCollapsed] = useState(true);
  const [salesManagerCollapsed, setSalesManagerCollapsed] = useState(true);
  const [salespersonCollapsed, setSalespersonCollapsed] = useState(true);
  const [fiManagerCollapsed, setFiManagerCollapsed] = useState(true);
  const [salesAnalysisCollapsed, setSalesAnalysisCollapsed] = useState(true);
  const [inventoryCollapsed, setInventoryCollapsed] = useState(true);
  const [usedInventoryCollapsed, setUsedInventoryCollapsed] = useState(true);
  const [consolidatedServiceCollapsed, setConsolidatedServiceCollapsed] = useState(false);
  const [consolidatedServiceSortConfig, setConsolidatedServiceSortConfig] = useState<ConsolidatedServiceSortConfig>({
    field: 'totalServiceGross',
    direction: 'desc',
  });
  const [consolidatedPartsCollapsed, setConsolidatedPartsCollapsed] = useState(false);
  const [consolidatedPartsSortConfig, setConsolidatedPartsSortConfig] = useState<ConsolidatedPartsSortConfig>({
    field: 'totalPartsGross',
    direction: 'desc',
  });

  // Reset department when location changes (prevents stale selection for body-shop-only locations)
  useEffect(() => {
    setSelectedDepartment(defaultDepartment);
  }, [locationId, defaultDepartment]);

  // Fetch forecast data
  // Pass selectedDepartment to get correct working days calculation for the selected department
  const {
    currentMonthData,
    historical,
    workingDays,
    displayWorkingDays,
    comparison,
    projectionTargets,
    unprocessedDeals,
    serviceGrossByPayType,
    serviceControllableExpenses,
    partsControllableExpenses,
    partsGrossByPayType,
    partsSalesByPayType,
    bodyShopGrossByCategory,
    consolidatedServiceByLocation,
    consolidatedPartsByLocation,
    dailySnapshots,
    consolidatedTrending,
    trendingByDepartment,
    availableYears,
    locationName,
    loading,
    refreshing,
    error,
    progressMessage,
    refreshData,
  } = useForecastData({ locationId, year, month, department: selectedDepartment });

  // Get current units at the comparison point (always total - not filtered by department)
  const currentUnits = comparison?.byYear[year]?.unitsAtPoint ?? null;

  // Check if this is the current month (can be refreshed)
  const isCurrentMonth = year === now.getFullYear() && month === (now.getMonth() + 1);

  // Determine if units card should be shown (only for ALL, NEW_VEHICLE, or USED_VEHICLE)
  const showUnitsCard = selectedDepartment === 'ALL' ||
    selectedDepartment === 'NEW_VEHICLE' ||
    selectedDepartment === 'USED_VEHICLE';

  // Check if we have historical data (at least 3 years)
  const hasHistoricalData = availableYears.length >= 3;

  // Locations where body shop has service advisors/technicians (no SERVICE dept)
  const bodyShopServiceLocations = [12, 44];

  // Service Advisor Performance - show for SERVICE department, or BODY_SHOP at locations 12/44
  const showServiceAdvisorPerformance =
    (selectedDepartment === 'SERVICE' ||
     (selectedDepartment === 'BODY_SHOP' && bodyShopServiceLocations.includes(locationId))) &&
    locationId !== 0 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive' || userRole === 'GeneralManager' || userRole === 'DepartmentManager' || userRole === 'TeamServiceManager' || userRole === 'MarketingManager');

  // Parts Department Analysis - show for PARTS department, or BODY_SHOP at locations where PARTS is consolidated (12)
  const showPartsAnalysis =
    (selectedDepartment === 'PARTS' ||
     (selectedDepartment === 'BODY_SHOP' && bodyShopServiceLocations.includes(locationId))) &&
    locationId !== 0 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive' || userRole === 'GeneralManager' || userRole === 'DepartmentManager' || userRole === 'TeamServiceManager' || userRole === 'MarketingManager');

  // Parts Counter Performance - show for PARTS department, or BODY_SHOP at location 12
  const showPartsCounterPerformance =
    (selectedDepartment === 'PARTS' ||
     (selectedDepartment === 'BODY_SHOP' && locationId === 12)) &&
    locationId !== 0 &&
    locationId !== 44 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive' || userRole === 'GeneralManager' || userRole === 'DepartmentManager' || userRole === 'TeamServiceManager' || userRole === 'MarketingManager');

  // Consolidated Service Analysis - show in consolidated view for SERVICE department (Admin/CEO/Executive only)
  const showConsolidatedServiceAnalysis =
    selectedDepartment === 'SERVICE' &&
    locationId === 0 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive');

  // Consolidated Parts Analysis - show in consolidated view for PARTS department (Admin/CEO/Executive only)
  const showConsolidatedPartsAnalysis =
    selectedDepartment === 'PARTS' &&
    locationId === 0 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive');

  // Fetch parts counter data when enabled
  const {
    persons: partsCounterPersons,
    summary: partsCounterSummary,
    loading: partsCounterLoading,
    refreshing: partsCounterRefreshing,
    error: partsCounterError,
    sortConfig: partsCounterSortConfig,
    setSortConfig: setPartsCounterSortConfig,
    refresh: refreshPartsCounterPerformance,
  } = usePartsCounterData({
    locationId,
    year,
    month,
    enabled: showPartsCounterPerformance,
  });

  // Fetch service advisor data when enabled
  const {
    advisors: serviceAdvisors,
    summary: serviceAdvisorSummary,
    loading: serviceAdvisorLoading,
    refreshing: serviceAdvisorRefreshing,
    error: serviceAdvisorError,
    sortConfig: serviceAdvisorSortConfig,
    setSortConfig: setServiceAdvisorSortConfig,
    refresh: refreshServiceAdvisorPerformance,
    appointmentsNext5Days: serviceAppointmentsNext5Days,
  } = useServiceAdvisorData({
    locationId,
    year,
    month,
    enabled: showServiceAdvisorPerformance,
  });

  // Technician Performance - SERVICE department or BODY_SHOP at locations 12/44
  const showTechnicianPerformance =
    (selectedDepartment === 'SERVICE' ||
     (selectedDepartment === 'BODY_SHOP' && bodyShopServiceLocations.includes(locationId))) &&
    locationId !== 0 &&
    (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive' || userRole === 'GeneralManager' || userRole === 'DepartmentManager' || userRole === 'TeamServiceManager' || userRole === 'MarketingManager');

  const {
    technicians,
    dailyByTech,
    summary: technicianSummary,
    loading: technicianLoading,
    refreshing: technicianRefreshing,
    error: technicianError,
    sortConfig: technicianSortConfig,
    setSortConfig: setTechnicianSortConfig,
    refresh: refreshTechnicianPerformance,
  } = useTechnicianData({
    locationId,
    year,
    month,
    enabled: showTechnicianPerformance,
  });

  // Sales Manager Performance - show for NEW_VEHICLE department, Admin/CEO/Executive/GM/DeptMgr/MarketingMgr
  const showSalesManagerPerformance =
    selectedDepartment === 'NEW_VEHICLE' &&
    locationId !== 0 &&
    !!userRole && ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'MarketingManager'].includes(userRole);

  const {
    managers: salesManagers,
    summary: salesManagerSummary,
    loading: salesManagerLoading,
    refreshing: salesManagerRefreshing,
    error: salesManagerError,
    sortConfig: salesManagerSortConfig,
    setSortConfig: setSalesManagerSortConfig,
    refresh: refreshSalesManagerPerformance,
  } = useSalesManagerData({
    locationId,
    year,
    month,
    enabled: showSalesManagerPerformance,
  });

  // vAuto Look to Book — only when the Sales Manager card itself is visible.
  // The endpoint already short-circuits untracked locations to available=false,
  // so we let the card decide whether to render the second pie via the data shape.
  const {
    data: lookToBookData,
    loading: lookToBookLoading,
  } = useVautoLookToBookData({
    locationId,
    enabled: showSalesManagerPerformance,
  });

  // Salesperson Performance - same gating as Sales Manager card
  const showSalespersonPerformance = showSalesManagerPerformance;

  // Sales Department Analysis (Good Leads MTD) - same gating as Sales Manager card
  const showSalesDepartmentAnalysis = showSalesManagerPerformance;

  // Inventory card - NEW_VEHICLE only, single location, same role gate.
  // Skips locations 12 and 44 (no new-vehicle inventory).
  const showInventoryCard =
    selectedDepartment === 'NEW_VEHICLE' &&
    locationId !== 0 &&
    locationId !== 12 &&
    locationId !== 44 &&
    !!userRole && ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'MarketingManager'].includes(userRole);

  const {
    data: inventoryAgingData,
    loading: inventoryAgingLoading,
    error: inventoryAgingError,
  } = useInventoryAgingData({
    locationId,
    enabled: showInventoryCard,
  });

  // Used Inventory card - USED_VEHICLE only, single location, same role gate.
  // Same 8 active locations as the New card; skips 12 and 44.
  const showUsedInventoryCard =
    selectedDepartment === 'USED_VEHICLE' &&
    locationId !== 0 &&
    locationId !== 12 &&
    locationId !== 44 &&
    !!userRole && ['Admin', 'CEO', 'Executive', 'GeneralManager', 'DepartmentManager', 'MarketingManager'].includes(userRole);

  const {
    data: usedInventoryAgingData,
    loading: usedInventoryAgingLoading,
    error: usedInventoryAgingError,
  } = useUsedInventoryAgingData({
    locationId,
    enabled: showUsedInventoryCard,
  });

  const {
    data: salesLeadsData,
    loading: salesLeadsLoading,
    error: salesLeadsError,
    closingRatioRows: salesLeadsClosingRatioRows,
    unmatchedCount: salesLeadsUnmatchedCount,
  } = useSalesLeadsData({
    locationId,
    year,
    month,
    enabled: showSalesDepartmentAnalysis,
  });

  const {
    data: salesAppointmentsData,
    loading: salesAppointmentsLoading,
    error: salesAppointmentsError,
  } = useSalesAppointmentsData({
    locationId,
    enabled: showSalesDepartmentAnalysis,
  });

  const {
    salespeople,
    teams: salespersonTeams,
    summary: salespersonSummary,
    loading: salespersonLoading,
    refreshing: salespersonRefreshing,
    error: salespersonError,
    sortConfig: salespersonSortConfig,
    setSortConfig: setSalespersonSortConfig,
    refresh: refreshSalespersonPerformance,
  } = useSalespersonData({
    locationId,
    year,
    month,
    enabled: showSalespersonPerformance,
  });

  // F&I Manager Performance - same gating as Sales Manager card, minus
  // MarketingManager (backend blocks the role on /fi-manager-performance).
  const showFIManagerPerformance =
    showSalesManagerPerformance && userRole !== 'MarketingManager';

  const {
    fiManagers,
    summary: fiManagerSummary,
    loading: fiManagerLoading,
    refreshing: fiManagerRefreshing,
    error: fiManagerError,
    sortConfig: fiManagerSortConfig,
    setSortConfig: setFiManagerSortConfig,
    refresh: refreshFIManagerPerformance,
  } = useFIManagerData({
    locationId,
    year,
    month,
    enabled: showFIManagerPerformance,
  });

  // Calculate department-specific gross values for comparison cards
  const comparisonGrossValues = useMemo(() => {
    if (!comparison || !workingDays) {
      return {
        currentGross: null,
        lastYearGross: null,
        lastYearFinalGross: null,
        lastYearHasData: false,
        averageGross: null,
        averageYearsIncluded: [] as number[],
        averageNote: undefined as string | undefined,
      };
    }

    // workingDays.elapsed counts days strictly before today (designed for 2am nightly loads).
    // On the first working day of a month, elapsed=0 but today's data may exist from manual refresh.
    // Fall back to WD 1 if elapsed is 0 and data exists at working day 1.
    const targetWorkingDay = workingDays.elapsed > 0
      ? workingDays.elapsed
      : (currentMonthData?.dailyEntries?.some(e => e.workingDayNumber === 1 && e.cumulativeGross !== 0) ? 1 : 0);

    // Current year gross (department-specific)
    const currentGross = getGrossAtWorkingDay(currentMonthData, targetWorkingDay, selectedDepartment);

    // Last year gross (department-specific)
    const lastYearSummary = historical[year - 1];
    const lastYearHasData = comparison.byYear[year - 1]?.hasData ?? false;

    // Calculate what working day to look at for last year (same days remaining)
    let lastYearGross: number | null = null;
    let lastYearFinalGross: number | null = null;

    if (lastYearHasData && lastYearSummary) {
      // For historical comparison, we need to find the working day with same "days remaining"
      const lastYearTotalWorkingDays = lastYearSummary.totalWorkingDays;
      const targetWorkingDayLastYear = lastYearTotalWorkingDays - comparison.daysRemaining;

      lastYearGross = getGrossAtWorkingDay(
        lastYearSummary,
        targetWorkingDayLastYear > 0 ? targetWorkingDayLastYear : 1,
        selectedDepartment
      );
      // Use accounting-based final month gross from DOC_GROSS_SUMMARY (matches Historical page)
      lastYearFinalGross = getDocFinalMonthGross(
        comparison.byYear[year - 1]?.docFinalMonthGross,
        selectedDepartment
      );
    }

    // Calculate 3-year average (department-specific)
    // Uses 50/30/20 weighting: Year-1 = 50%, Year-2 = 30%, Year-3 = 20%
    const historicalYears = [year - 1, year - 2, year - 3];
    const baseWeights = [0.50, 0.30, 0.20];
    const grossData: { gross: number; finalGross: number; weight: number; year: number }[] = [];

    for (let i = 0; i < historicalYears.length; i++) {
      const histYear = historicalYears[i];
      const histSummary = historical[histYear];
      const histYearData = comparison.byYear[histYear];
      if (histSummary && histYearData?.hasData) {
        const histTotalWorkingDays = histSummary.totalWorkingDays;
        const targetWorkingDayHist = histTotalWorkingDays - comparison.daysRemaining;

        const histGross = getGrossAtWorkingDay(
          histSummary,
          targetWorkingDayHist > 0 ? targetWorkingDayHist : 1,
          selectedDepartment
        );

        // Use accounting-based final month gross from DOC_GROSS_SUMMARY (matches Historical page)
        const histFinalGross = getDocFinalMonthGross(histYearData.docFinalMonthGross, selectedDepartment);

        if (histGross !== null && histGross !== 0 && histFinalGross !== null && histFinalGross !== 0) {
          grossData.push({
            gross: histGross,
            finalGross: histFinalGross,
            weight: baseWeights[i],
            year: histYear,
          });
        }
      }
    }

    const yearsIncluded = grossData.map(d => d.year);

    // Calculate weighted averages (normalize weights if we don't have all 3 years)
    let averageGross: number | null = null;
    let averageFinalGross: number | null = null;

    if (grossData.length > 0) {
      const totalWeight = grossData.reduce((sum, d) => sum + d.weight, 0);
      averageGross = grossData.reduce((sum, d) => sum + (d.gross * d.weight / totalWeight), 0);
      averageFinalGross = grossData.reduce((sum, d) => sum + (d.finalGross * d.weight / totalWeight), 0);
    }

    const averageNote = yearsIncluded.length < 3 && yearsIncluded.length > 0
      ? `Based on ${yearsIncluded.length} year(s) of available data`
      : yearsIncluded.length === 0
        ? 'No historical data available'
        : undefined;

    return {
      currentGross,
      lastYearGross,
      lastYearFinalGross,
      lastYearHasData,
      averageGross,
      averageFinalGross,
      averageYearsIncluded: yearsIncluded,
      averageNote,
    };
  }, [comparison, workingDays, currentMonthData, historical, year, selectedDepartment]);

  // Calculate unprocessed gross for the selected department
  const unprocessedGross = useMemo(() => {
    if (!unprocessedDeals) return null;

    if (selectedDepartment === 'ALL') {
      return unprocessedDeals.totalGross;
    } else if (selectedDepartment === 'NEW_VEHICLE') {
      return unprocessedDeals.byDepartment.NEW_VEHICLE;
    } else if (selectedDepartment === 'USED_VEHICLE') {
      return unprocessedDeals.byDepartment.USED_VEHICLE;
    }
    // Other departments don't have unprocessed deals
    return null;
  }, [unprocessedDeals, selectedDepartment]);

  // Prepare unprocessed units for UnitCountsCard (excludes swaps — backend separates them)
  const unprocessedUnitsForCard = useMemo(() => {
    if (!unprocessedDeals) return undefined;

    return {
      unitsByBrand: unprocessedDeals.unitsByBrand ?? {},
      unitsByCategory: unprocessedDeals.unitsByCategory ?? {},
      totalNewUnits: unprocessedDeals.totalNewUnits ?? 0,
      totalUsedUnits: unprocessedDeals.totalUsedUnits ?? 0,
    };
  }, [unprocessedDeals]);

  // Swap units (New Vehicles only — dealer trades tracked separately from sold units)
  const swapNewUnits = useMemo(() => {
    return unprocessedDeals?.swapNewUnits ?? 0;
  }, [unprocessedDeals]);

  // Wholesale units (Used Vehicles only — wholesale deals tracked separately from sold units)
  const wholesaleUsedUnits = useMemo(() => {
    return unprocessedDeals?.wholesaleUsedUnits ?? 0;
  }, [unprocessedDeals]);

  // Calculate trending values for the 2026 Gross tile
  // Uses Time-Weighted Blend: blends current pace with historical average
  // Early in month: heavily weighted to historical; late in month: heavily weighted to current pace
  const trendingValues = useMemo(() => {
    // Only calculate for current month
    if (!isCurrentMonth) {
      return { trendingGross: null, projectedExpenses: null, trendingProfit: null };
    }

    const { currentGross, averageFinalGross } = comparisonGrossValues;
    const projectedExpenses = projectionTargets?.totalExpenses ?? null;

    // Prefer backend-computed per-department trends, which use each dept's own working
    // days. Trend math doesn't distribute over departments with different schedules
    // (or, for consolidated, over locations with different schedules), so trend-of-sum
    // (the path below) is mathematically wrong for ALL on consolidated AND on individual
    // locations. Single-department views were correct already, but using this field
    // keeps both code paths consistent.
    const backendTrending = isConsolidated
      ? consolidatedTrending?.byDepartment
      : trendingByDepartment;
    if (backendTrending) {
      const deptKey = selectedDepartment as keyof typeof backendTrending;
      const summed = backendTrending[deptKey];
      if (typeof summed === 'number' && Number.isFinite(summed)) {
        return {
          trendingGross: summed,
          projectedExpenses,
          trendingProfit: projectedExpenses !== null ? summed - projectedExpenses : null,
        };
      }
    }

    // currentGross from DAILY_GROSS_SUMMARY already includes all sold deals
    // (processed + unprocessed) via the unified COALESCE query
    const currentActual = currentGross ?? 0;

    // Get working days info - use department-specific for elapsed/total
    // Same first-working-day fallback as comparisonGrossValues
    let daysElapsed = displayWorkingDays?.elapsed ?? workingDays?.elapsed ?? 0;
    if (daysElapsed === 0 && currentMonthData?.dailyEntries?.some(e => e.workingDayNumber === 1 && e.cumulativeGross !== 0)) {
      daysElapsed = 1;
    }
    const totalWorkingDays = displayWorkingDays?.total ?? workingDays?.total ?? 0;

    // Get historical average at month end (from comparisonGrossValues)
    const historicalAvgAtMonthEnd = averageFinalGross ?? null;

    // Need working days and historical data to calculate trend
    if (daysElapsed <= 0 || totalWorkingDays <= 0) {
      return {
        trendingGross: historicalAvgAtMonthEnd,
        projectedExpenses,
        trendingProfit: projectedExpenses !== null && historicalAvgAtMonthEnd !== null
          ? historicalAvgAtMonthEnd - projectedExpenses
          : null,
      };
    }

    // Calculate trending gross using Time-Weighted Blend
    const trendingGross = calculateTrendingGross(
      currentActual,
      daysElapsed,
      totalWorkingDays,
      historicalAvgAtMonthEnd ?? 0
    );

    // Calculate trending profit
    const trendingProfit = projectedExpenses !== null
      ? trendingGross - projectedExpenses
      : null;

    return { trendingGross, projectedExpenses, trendingProfit };
  }, [isCurrentMonth, isConsolidated, consolidatedTrending, trendingByDepartment, selectedDepartment, comparisonGrossValues, displayWorkingDays, workingDays, projectionTargets, currentMonthData]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daily Gross Progression</h1>
            <p className="text-gray-500">
              Track gross accumulation and compare to historical performance
            </p>
          </div>
        </div>
      </div>

      {/* Control Panel */}
      <ForecastControlPanel
        locationId={locationId}
        year={year}
        month={month}
        onLocationChange={setLocationId}
        onYearChange={setYear}
        onMonthChange={setMonth}
        onRefresh={refreshData}
        loading={loading}
        refreshing={refreshing}
        lastRefreshed={currentMonthData?.lastRefreshed}
        hasHistoricalData={hasHistoricalData}
        canRefresh={showRefreshButton}
      />

      {/* Department Tabs */}
      <DepartmentTabs
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}
        locationId={locationId}
        assignedDepartmentIds={hasDeptRestriction ? effectiveDepartmentIds : undefined}
      />

      {/* Working Days Progress Bar (slim) - always visible when data exists */}
      {workingDays && !loading && !error && (
        <div className="mb-4">
          <WorkingDaysProgress
            total={displayWorkingDays?.total ?? workingDays.total}
            elapsed={displayWorkingDays?.elapsed ?? workingDays.elapsed}
            remaining={displayWorkingDays?.remaining ?? workingDays.remaining}
            currentDayNumber={workingDays.currentDayNumber}
            slim={true}
          />
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading progression data...</p>
        </div>
      )}

      {/* Refreshing Progress Banner */}
      {refreshing && progressMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <LoadingSpinner />
            <p className="text-blue-700">{progressMessage}</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Data</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
              <button
                onClick={() => refreshData()}
                className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <>
          {/* No Data State */}
          {!currentMonthData && !Object.values(historical).some(h => h !== null) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                <div>
                  <h3 className="text-yellow-800 font-medium">No Data Available</h3>
                  <p className="text-yellow-700 text-sm mt-1">
                    No gross progression data is available for {locationName} in this period.
                    {isCurrentMonth && ' Click "Refresh Data" to load data from DealerBuilt.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Gross Comparison Cards + Projection Tile */}
          {comparison && (
            <div className="mb-6">
              <GrossComparisonCards
                currentYear={year}
                currentGross={comparisonGrossValues.currentGross}
                lastYearGross={comparisonGrossValues.lastYearGross}
                lastYearFinalGross={comparisonGrossValues.lastYearFinalGross}
                lastYearHasData={comparisonGrossValues.lastYearHasData}
                averageGross={comparisonGrossValues.averageGross}
                averageFinalGross={comparisonGrossValues.averageFinalGross}
                averageYearsIncluded={comparisonGrossValues.averageYearsIncluded}
                averageNote={comparisonGrossValues.averageNote}
                daysRemaining={displayWorkingDays?.remaining ?? comparison.daysRemaining}
                selectedDepartment={selectedDepartment}
                unprocessedGross={unprocessedGross}
                trendingGross={trendingValues.trendingGross}
                projectedExpenses={trendingValues.projectedExpenses}
                trendingProfit={trendingValues.trendingProfit}
                isCurrentMonth={isCurrentMonth}
                hideExpenseProfit={isMarketingManager}
                projectionTile={projectionTargets ? (
                  <ProjectionsTile
                    month={month}
                    year={year}
                    department={selectedDepartment}
                    projectionData={projectionTargets}
                    mode="card"
                    hideExpenseProfit={isMarketingManager}
                  />
                ) : undefined}
              />
            </div>
          )}

          {/* Unit Counts Card - only for vehicle departments */}
          {showUnitsCard && currentUnits && comparison && (
            <div className="mb-6">
              <UnitCountsCard
                currentYear={year}
                currentUnits={currentUnits}
                lastYearData={comparison.byYear[year - 1] || null}
                comparisonByYear={comparison.byYear}
                daysRemaining={displayWorkingDays?.remaining ?? comparison.daysRemaining}
                daysElapsed={displayWorkingDays?.elapsed ?? workingDays?.elapsed}
                totalWorkingDays={displayWorkingDays?.total ?? workingDays?.total}
                selectedDepartment={selectedDepartment}
                locationId={locationId}
                unprocessedUnits={unprocessedUnitsForCard}
                swapNewUnits={swapNewUnits}
                wholesaleUsedUnits={wholesaleUsedUnits}
                targetUnits={projectionTargets?.units}
                onUnitsClick={
                  !isConsolidated &&
                  (userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive' || userRole === 'GeneralManager' || userRole === 'DepartmentManager' || userRole === 'MarketingManager') &&
                  selectedDepartment !== 'ALL'
                    ? (dept) => setDealDetailsModal({ isOpen: true, department: dept })
                    : undefined
                }
              />
            </div>
          )}

          {/* Inventory (NEW dept only, single location) */}
          {showInventoryCard && (
            <div className="mb-6">
              <InventoryAgingCard
                data={inventoryAgingData}
                loading={inventoryAgingLoading}
                error={inventoryAgingError}
                locationName={locationName}
                collapsed={inventoryCollapsed}
                onToggleCollapse={() => setInventoryCollapsed(prev => !prev)}
              />
            </div>
          )}

          {/* Inventory (USED dept only, single location) */}
          {showUsedInventoryCard && (
            <div className="mb-6">
              <UsedInventoryAgingCard
                data={usedInventoryAgingData}
                loading={usedInventoryAgingLoading}
                error={usedInventoryAgingError}
                locationName={locationName}
                collapsed={usedInventoryCollapsed}
                onToggleCollapse={() => setUsedInventoryCollapsed(prev => !prev)}
              />
            </div>
          )}

          {/* Sales Department Analysis (Good Leads MTD + future tiles) */}
          {showSalesDepartmentAnalysis && (
            <div className="mb-6">
              <SalesDepartmentAnalysis
                locationId={locationId}
                leadsData={salesLeadsData}
                loading={salesLeadsLoading}
                error={salesLeadsError}
                year={year}
                month={month}
                closingRatioRows={salesLeadsClosingRatioRows}
                unmatchedCount={salesLeadsUnmatchedCount}
                appointmentsData={salesAppointmentsData}
                appointmentsLoading={salesAppointmentsLoading}
                appointmentsError={salesAppointmentsError}
                collapsed={salesAnalysisCollapsed}
                onToggleCollapse={() => setSalesAnalysisCollapsed(prev => !prev)}
              />
            </div>
          )}

          {/* Sales Manager Performance */}
          {showSalesManagerPerformance && (
            <div className="mb-6">
              <SalesManagerCard
                managers={salesManagers}
                summary={salesManagerSummary}
                loading={salesManagerLoading}
                refreshing={salesManagerRefreshing}
                error={salesManagerError}
                year={year}
                month={month}
                sortConfig={salesManagerSortConfig}
                onSortChange={setSalesManagerSortConfig}
                onRefresh={showRefreshButton ? refreshSalesManagerPerformance : undefined}
                showHeaderTotals={userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive'}
                collapsed={salesManagerCollapsed}
                onToggleCollapse={() => setSalesManagerCollapsed(prev => !prev)}
                lookToBookData={lookToBookData}
                lookToBookLoading={lookToBookLoading}
              />
            </div>
          )}

          {/* Salesperson Performance */}
          {showSalespersonPerformance && (
            <div className="mb-6">
              <SalespersonCard
                salespeople={salespeople}
                teams={salespersonTeams}
                summary={salespersonSummary}
                loading={salespersonLoading}
                refreshing={salespersonRefreshing}
                error={salespersonError}
                year={year}
                month={month}
                sortConfig={salespersonSortConfig}
                onSortChange={setSalespersonSortConfig}
                onRefresh={showRefreshButton ? refreshSalespersonPerformance : undefined}
                showHeaderTotals={userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive'}
                collapsed={salespersonCollapsed}
                onToggleCollapse={() => setSalespersonCollapsed(prev => !prev)}
              />
            </div>
          )}

          {/* F&I Manager Performance */}
          {showFIManagerPerformance && (
            <div className="mb-6">
              <FIManagerCard
                fiManagers={fiManagers}
                summary={fiManagerSummary}
                loading={fiManagerLoading}
                refreshing={fiManagerRefreshing}
                error={fiManagerError}
                year={year}
                month={month}
                sortConfig={fiManagerSortConfig}
                onSortChange={setFiManagerSortConfig}
                onRefresh={showRefreshButton ? refreshFIManagerPerformance : undefined}
                collapsed={fiManagerCollapsed}
                onToggleCollapse={() => setFiManagerCollapsed(prev => !prev)}
              />
            </div>
          )}

          {/* Parts Gross Breakdown */}
          {showPartsAnalysis && partsGrossByPayType && (
            <div className="mb-6">
              <PartsGrossBreakdown
                partsGrossByPayType={partsGrossByPayType}
                partsSalesByPayType={partsSalesByPayType}
                partsControllableExpenses={partsControllableExpenses}
                totalPartsGross={
                  partsGrossByPayType.customerParts +
                  partsGrossByPayType.warrantyParts +
                  partsGrossByPayType.internalParts +
                  partsGrossByPayType.counterRetail +
                  partsGrossByPayType.counterWholesale +
                  partsGrossByPayType.otherParts +
                  partsGrossByPayType.inventoryAdjustment
                }
                year={year}
                month={month}
                collapsed={partsAnalysisCollapsed}
                onToggleCollapse={() => setPartsAnalysisCollapsed(prev => !prev)}
                lastRefreshed={currentMonthData?.lastRefreshed ?? currentMonthData?.loadedAt}
              />
            </div>
          )}

          {/* Consolidated Parts Analysis by Location - consolidated view only */}
          {showConsolidatedPartsAnalysis && consolidatedPartsByLocation && consolidatedPartsByLocation.length > 0 && (
            <div className="mb-6">
              <ConsolidatedPartsAnalysis
                data={consolidatedPartsByLocation}
                year={year}
                month={month}
                collapsed={consolidatedPartsCollapsed}
                onToggleCollapse={() => setConsolidatedPartsCollapsed(prev => !prev)}
                sortConfig={consolidatedPartsSortConfig}
                onSortChange={setConsolidatedPartsSortConfig}
              />
            </div>
          )}

          {/* Parts Counter Performance */}
          {showPartsCounterPerformance && (
            <div className="mb-6">
              <PartsCounterCard
                persons={partsCounterPersons}
                summary={partsCounterSummary}
                loading={partsCounterLoading}
                refreshing={partsCounterRefreshing}
                error={partsCounterError}
                year={year}
                month={month}
                sortConfig={partsCounterSortConfig}
                onSortChange={setPartsCounterSortConfig}
                onRefresh={showRefreshButton ? refreshPartsCounterPerformance : undefined}
                collapsed={partsCounterCollapsed}
                onToggleCollapse={() => setPartsCounterCollapsed(prev => !prev)}
                showHeaderTotals={userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive'}
              />
            </div>
          )}

          {/* Service Gross Breakdown - same visibility as SA Performance */}
          {showServiceAdvisorPerformance && serviceGrossByPayType && (
            <div className="mb-6">
              <ServiceGrossBreakdown
                serviceGrossByPayType={serviceGrossByPayType}
                serviceControllableExpenses={serviceControllableExpenses}
                totalServiceGross={
                  serviceGrossByPayType.customer +
                  serviceGrossByPayType.warranty +
                  serviceGrossByPayType.internal +
                  serviceGrossByPayType.other
                }
                year={year}
                month={month}
                collapsed={serviceAnalysisCollapsed}
                onToggleCollapse={() => setServiceAnalysisCollapsed(prev => !prev)}
                closedROCount={serviceAdvisorSummary?.totalROCount}
                openROCount={serviceAdvisorSummary?.totalOpenROCount}
                openROAgeBuckets={serviceAdvisorSummary?.totalOpenROAgeBuckets}
                appointmentsNext5Days={serviceAppointmentsNext5Days ?? undefined}
              />
            </div>
          )}

          {/* Consolidated Service Analysis by Location - consolidated view only */}
          {showConsolidatedServiceAnalysis && consolidatedServiceByLocation && consolidatedServiceByLocation.length > 0 && (
            <div className="mb-6">
              <ConsolidatedServiceAnalysis
                data={consolidatedServiceByLocation}
                year={year}
                month={month}
                collapsed={consolidatedServiceCollapsed}
                onToggleCollapse={() => setConsolidatedServiceCollapsed(prev => !prev)}
                sortConfig={consolidatedServiceSortConfig}
                onSortChange={setConsolidatedServiceSortConfig}
              />
            </div>
          )}

          {/* Service Advisor Performance */}
          {showServiceAdvisorPerformance && (
            <div className="mb-6">
              <ServiceAdvisorCard
                advisors={serviceAdvisors}
                summary={serviceAdvisorSummary}
                loading={serviceAdvisorLoading}
                refreshing={serviceAdvisorRefreshing}
                error={serviceAdvisorError}
                year={year}
                month={month}
                sortConfig={serviceAdvisorSortConfig}
                onSortChange={setServiceAdvisorSortConfig}
                onRefresh={showRefreshButton ? refreshServiceAdvisorPerformance : undefined}
                collapsed={serviceAdvisorCollapsed}
                onToggleCollapse={() => setServiceAdvisorCollapsed(prev => !prev)}
                showHeaderTotals={userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive'}
              />
            </div>
          )}

          {/* Technician Performance */}
          {showTechnicianPerformance && (
            <div className="mb-6">
              <TechnicianCard
                technicians={technicians}
                dailyByTech={dailyByTech}
                summary={technicianSummary}
                loading={technicianLoading}
                refreshing={technicianRefreshing}
                error={technicianError}
                year={year}
                month={month}
                sortConfig={technicianSortConfig}
                onSortChange={setTechnicianSortConfig}
                onRefresh={showRefreshButton ? refreshTechnicianPerformance : undefined}
                collapsed={technicianCollapsed}
                onToggleCollapse={() => setTechnicianCollapsed(prev => !prev)}
                showHeaderTotals={userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive'}
              />
            </div>
          )}

          {/* Body Shop Labor vs Parts breakdown — only on the BODY_SHOP tab, and only when
              the backend supplied the split (locations 1, 12, 44). Sits directly above the
              progression chart so the labor/parts numbers read alongside the chart trend. */}
          {selectedDepartment === 'BODY_SHOP' && bodyShopGrossByCategory && (
            <BodyShopGrossBreakdown bodyShopGrossByCategory={bodyShopGrossByCategory} />
          )}

          {/* Chart Section */}
          {(currentMonthData || Object.values(historical).some(h => h !== null)) && (
            <div className="mb-6">
              <GrossProgressionChart
                currentYear={year}
                currentMonthData={currentMonthData}
                historical={historical}
                currentWorkingDay={workingDays?.currentDayNumber ?? 0}
                currentYearTotalWorkingDays={displayWorkingDays?.total ?? workingDays?.total}
                selectedDepartment={selectedDepartment}
                projectionTargets={projectionTargets ?? undefined}
                unprocessedByDate={unprocessedDeals?.byDate}
                trendingEndValue={trendingValues.trendingGross}
                lastYearFinalGross={comparisonGrossValues.lastYearFinalGross}
                averageFinalGross={comparisonGrossValues.averageFinalGross}
                currentYearGrossAtPoint={comparisonGrossValues.currentGross}
                dailySnapshots={dailySnapshots ?? undefined}
              />
            </div>
          )}

          {/* Historical Data Note */}
          {availableYears.length > 0 && availableYears.length < 4 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600">
                Historical comparison based on {availableYears.length} year(s) of available data
                ({availableYears.join(', ')})
              </p>
            </div>
          )}
        </>
      )}

      {/* Deal Details Modal - Admin/Executive/GM only */}
      {dealDetailsModal && (
        <DealDetailsModal
          isOpen={dealDetailsModal.isOpen}
          onClose={() => setDealDetailsModal(null)}
          locationId={locationId}
          locationName={locationName}
          year={year}
          month={month}
          department={dealDetailsModal.department}
        />
      )}
    </div>
  );
};
